import {lazy, Suspense} from "react";
import {Navigate, Outlet, useRoutes} from "react-router-dom";
import GuestGuard from "./guards/GuestGuard";
import AuthGuard from "./guards/AuthGuard";
import {LoadingScreen} from "@bounce/shared/ui";
import {urls} from "./urls";
import {componentLoader} from "@bounce/shared/helpers";
import AccountLayout from "../layout/account/AccountLayout";
import {useUser} from "../contexts";
import UiGuard from "./guards/UiGuard";
import BackofficeLayout from "../layout/agent";
import BackofficeGuard from "./guards/BackofficeGuard";
import SpringLayout from "../layout/spring/SpringLayout";
import LoginPage from "../pages/backoffice/common/LoginPage";
import OnboardingLayout from "../layout/account/OnboardingLayout";

const Loadable = (Component: any) => (props: any) => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const {loggedIn} = useUser();
  return useRoutes([
    {
      path: "/",
      element: <Navigate to={loggedIn ? urls.accounts.url : urls.login.url} replace />,
    },
    // Multi Accounts
    {
      path: urls.accounts.path,
      element: (
        <AuthGuard>
          <UiGuard>
            <AccountLayout />
          </UiGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: "",
          element: <Accounts />,
        },
      ],
    },
    // Backoffice Routes
    {
      path: urls.backofficeLogin.path,
      element: <LoginPage />,
    },
    {
      path: urls.backoffice.path,
      element: (
        <BackofficeGuard>
          <BackofficeLayout />
        </BackofficeGuard>
      ),
      children: [
        {
          path: urls.search.path,
          element: <Search />,
        },
        {
          path: urls.informationPanel.path,
          element: <InformationPanel />,
        },
        {
          path: urls.history.path,
          element: <RexHistory />,
        },
        {
          path: urls.targetBreakdown.path,
          element: <TargetBreakdown />,
        },
        {path: "*", element: <Navigate to={urls.error.url} replace />},
        {
          path: "",
          element: <Navigate to={urls.search.url} replace />,
        },
      ],
    },
    {
      path: urls.spring.path,
      element: (
        <BackofficeGuard>
          <SpringLayout />
        </BackofficeGuard>
      ),
      children: [
        {
          path: urls.dscBulkOperations.path,
          element: <BulkOperations />,
        },
        {
          path: urls.importPricing.path,
          element: <ImportPricing />,
        },
        {
          path: urls.generatePricing.path,
          element: <GeneratePricingWashReport />,
        },
        {
          path: urls.anonymousCommunications.path,
          element: <AnonymousCommunications />,
        },
        {
          path: urls.dscOverview.path,
          element: <DSCOverview />,
        },
        {
          path: urls.representationCompanies.path,
          element: <RepresentationCompanies />,
        },
        {
          path: urls.searchSpring.path,
          element: <Search />,
        },
        {
          path: urls.informationPanelSpring.path,
          element: <InformationPanel />,
        },
        {
          path: urls.accessManagement.path,
          element: <AccessManagement />,
        },
        {
          path: urls.userLevelMedia.path,
          element: <UserLevelMedia />,
        },
        {
          path: urls.batchLevelMedia.path,
          element: <BatchLevelMedia />,
        },
        {
          path: urls.partners.path,
          element: <PartnersList />,
        },
        {
          path: urls.paymentsCredentials.path,
          element: <PaymentsCredentialsList />,
        },
        {
          path: urls.addPaymentsCredentials.path,
          element: <AddPaymentsCredentials />,
        },
        {
          path: urls.addPartner.path,
          element: <AddPartner />,
        },
        {
          path: urls.editPartner.path,
          element: <EditPartner />,
        },
        {
          path: urls.importUsers.path,
          element: <ImportUsers />,
        },
        {
          path: urls.importPayments.path,
          element: <ImportPayments />,
        },
        {
          path: urls.washing.path,
          element: <Washing />,
        },
        {
          path: urls.bankruptcies.path,
          element: <Bankruptcies />,
        },
        {
          path: urls.inventory.path,
          element: <Inventory />,
        },
        {
          path: urls.legalCollectionsBulkOperation.path,
          element: <LegalCollectionsBulkOperation />,
        },
        {
          path: urls.disputes.path,
          element: <Disputes />,
        },
        {
          path: urls.disputesDashboard.path,
          element: <DisputesDashboard />,
        },
        {
          path: urls.partnerPlatformBulkOperation.path,
          element: <PartnerPlatformBulkOperations />,
        },
        {
          path: urls.partnerPlatformReports.path,
          element: <Reports />,
        },
        {
          path: urls.managePartnerUsers.path,
          element: <ManagePartnerUsers />,
        },

        {
          path: urls.conversationsSpring.path,
          element: <RexHistory />,
        },
        {
          path: "",
          element: <Navigate to={urls.searchSpring.url} replace />,
        },
      ],
    },

    // Auth Routes
    {
      path: urls.auth.path,
      element: (
        <GuestGuard>
          <UiGuard>
            <AccountLayout />
          </UiGuard>
        </GuestGuard>
      ),
      children: [
        {
          path: urls.login.path,
          element: <Login />,
        },
        {
          path: urls.deprecatedLogin.path,
          element: <Login />,
        },
        {
          path: urls.verifyAccount.path,
          element: <VerifyAccount />,
        },
        {
          path: urls.verifyCodeFromUrl.path,
          element: <VerifyCode />,
        },
        {
          path: urls.verifyCodeFromUrlWithCampaign.path,
          element: <VerifyCode />,
        },
        {
          path: urls.verifyAnonymousAccount.path,
          element: <VerifyAnonymousAccount />,
        },
        {
          path: urls.verifyCode.path,
          element: <VerifyCode />,
        },
        {path: "", element: <Navigate to={urls.error.url} replace />},
        {path: "*", element: <Navigate to={urls.error.url} replace />},
      ],
    },
    // Account Routes
    {
      path: urls.account.path,
      element: (
        <AuthGuard>
          <UiGuard>
            <AccountLayout />
          </UiGuard>
        </AuthGuard>
      ),
      // Active plan pages
      children: [
        {
          path: "",
          element: <Accounts />,
        },
        {
          path: urls.account.path,
          element: <Accounts />,
        },
        {
          path: urls.accountMainNoRedirect.path,
          element: <Account />,
        },
        {
          path: urls.accountDashboard.path,
          element: <AccountDashboardPage />,
        },
        {
          path: urls.paymentSuccess.path,
          element: <PaymentSuccess />,
        },
        {
          path: urls.disputeSuccess.path,
          element: <DisputeSuccess />,
        },
        {
          path: urls.rewards.path,
          element: <Rewards />,
        },
        {
          path: urls.dispute.path,
          element: <DisputePage />,
        },
        {
          path: urls.hardshipPaymentAfterFailureSummary.path,
          element: <HardshipPaymentAfterFailureSummary />,
        },
        {
          path: urls.hardshipExtensionSummary.path,
          element: <HardshipExtensionSummary />,
        },
        {
          path: urls.expiredCampaign.path,
          element: <ExpiredCampaign />,
        },
      ],
    },
    // On boarding pages
    {
      path: urls.account.path,
      element: (
        <AuthGuard>
          <UiGuard>
            <AccountLayout>
              <OnboardingLayout />
            </AccountLayout>
          </UiGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: urls.accountMain.path,
          element: <Account />,
        },
        {
          path: urls.summaryPage.path,
          element: <SummaryPage />,
        },
        {
          path: urls.verificationRequest.path,
          element: <VerificationRequestPage />,
        },
        {
          path: urls.presetPlanCheckout.path,
          element: <PresetPlanCheckout />,
        },
        {
          path: urls.calendarReminders.path,
          element: <CalendarReminders />,
        },
        {path: "*", element: <Navigate to={urls.error.url} replace />},
        // Payment plan
        {
          path: urls.choosePlan.path,
          element: <ChoosePlan />,
        },
        {
          path: urls.partialPayment.path,
          element: <CustomPayment />,
        },
        {
          path: urls.microDownPayment.path,
          element: <MicroDownPayment />,
        },
        {
          path: urls.hardshipIntro.path,
          element: <HardshipIntro />,
        },
        {
          path: urls.hardshipPlans.path,
          element: <HardshipPlans />,
        },
        {
          path: urls.reactivatePlan.path,
          element: <ReactivatePlan />,
        },
        {
          path: urls.taxRefundWizard.path,
          element: <TaxRefundWizard />,
        },
      ],
    },
    // Guest payment plan checkout routes
    {
      path: urls.guestPaymentPlanRoot.path,
      element: (
        <GuestGuard unknownUser={true}>
          <UiGuard>
            <AccountLayout />
          </UiGuard>
        </GuestGuard>
      ),
      children: [
        {
          path: urls.guestPaymentPlanCheckout.path,
          element: <GuestPaymentPlanCheckout />,
        },
        {
          path: urls.guestMatrixPaymentPlanCheckout.path,
          element: <GuestMatrixPaymentPlanCheckout />,
        },
        {
          path: urls.guestPaymentSuccess.path,
          element: <PaymentSuccess />,
        },
      ],
    },

    // Guest payment checkout routes
    {
      path: urls.guestPaymentRoot.path,
      element: (
        <GuestGuard unknownUser={true}>
          <UiGuard>
            <AccountLayout />
          </UiGuard>
        </GuestGuard>
      ),
      children: [
        {
          path: urls.guestPaymentCheckout.path,
          element: <GuestPaymentCheckout />,
        },
        {
          path: urls.guestPaymentSuccess.path,
          element: <PaymentSuccess />,
        },
      ],
    },
    // General Routes
    {
      path: "*",
      element: <Outlet />,
      children: [
        {path: urls.error.path, element: <Error />},
        {path: "*", element: <Navigate to={urls.error.url} replace />},
      ],
    },
    {path: "*", element: <Navigate to={urls.error.url} replace />},
  ]);
}

// IMPORT COMPONENTS

// AUTH
const Login = Loadable(lazy(() => componentLoader(() => import("../pages/auth/login/LoginTest"))));
const VerifyCode = Loadable(lazy(() => componentLoader(() => import("../pages/auth/login/VerifyCode"))));
const VerifyAccount = Loadable(lazy(() => componentLoader(() => import("../pages/auth/login/VerifyAccount"))));
const VerifyAnonymousAccount = Loadable(
  lazy(() => componentLoader(() => import("../pages/auth/login/VerifyAnonymousAccount")))
);

// BACKOFFICE
const Search = Loadable(lazy(() => componentLoader(() => import("../pages/backoffice/search/Search"))));

const RexHistory = Loadable(
  lazy(() => componentLoader(() => import("../pages/backoffice/rex-conversations/RexConversations")))
);
const InformationPanel = Loadable(
  lazy(() => componentLoader(() => import("../pages/backoffice/information-panel/InformationPanel")))
);

// SPRING
const UserLevelMedia = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/partner-operations/UserLevelMedia")))
);
const BatchLevelMedia = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/partner-operations/media-batch/BatchLevelMedia")))
);

const ImportUsers = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/partner-operations/ImportUsers")))
);
const Washing = Loadable(lazy(() => componentLoader(() => import("../pages/spring/partner-operations/Washing"))));
const Disputes = Loadable(lazy(() => componentLoader(() => import("../pages/spring/disputes/Disputes"))));
const DisputesDashboard = Loadable(lazy(() => componentLoader(() => import("../pages/spring/disputes/Dashboard"))));
const ImportPayments = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/partner-operations/ImportPayments")))
);

const ImportPricing = Loadable(lazy(() => componentLoader(() => import("../pages/spring/pricing/ImportPricing"))));
const GeneratePricingWashReport = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/pricing/GeneratePricingWashReport")))
);
const AccessManagement = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/spring-users-management/SpringAccessManagement")))
);

const Bankruptcies = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/bankruptcies/BankruptciesPanel")))
);

const Inventory = Loadable(lazy(() => componentLoader(() => import("../pages/spring/inventory/Inventory"))));
const TargetBreakdown = Loadable(
  lazy(() => componentLoader(() => import("../pages/backoffice/target-breakdown/TargetBreakdown")))
);
const LegalCollectionsBulkOperation = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/legal-collections/LegalCollectionsBulkOperation")))
);

const BulkOperations = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/dsc/bulk-operations/BulkOperations")))
);
const RepresentationCompanies = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/representation-companies/RepresentationCompanies")))
);
const PartnersList = Loadable(lazy(() => componentLoader(() => import("../pages/spring/partners/PartnersList"))));
const PaymentsCredentialsList = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/payments-credentials/PaymentsCredentialsList")))
);
const AddPaymentsCredentials = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/payments-credentials/AddPaymentsCredentials")))
);
const AddPartner = Loadable(lazy(() => componentLoader(() => import("../pages/spring/partners/AddPartner"))));
const EditPartner = Loadable(lazy(() => componentLoader(() => import("../pages/spring/partners/EditPartner"))));
const AnonymousCommunications = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/dsc/AnonymousCommunications")))
);
const DSCOverview = Loadable(lazy(() => componentLoader(() => import("../pages/spring/dsc/DSCOverview"))));

const Reports = Loadable(lazy(() => componentLoader(() => import("../pages/spring/partner-platform/reports/Reports"))));
const ManagePartnerUsers = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/partner-platform/manage-users/ManagePartnerUsers")))
);
const PartnerPlatformBulkOperations = Loadable(
  lazy(() => componentLoader(() => import("../pages/spring/partner-platform/bulk-operations/BulkOperations")))
);

// ACCOUNT
const Account = Loadable(lazy(() => componentLoader(() => import("../pages/account/account/Account"))));
const Accounts = Loadable(lazy(() => componentLoader(() => import("../pages/account/accounts/Accounts"))));
const AccountDashboardPage = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/account-dashboard/AccountDashboardPage")))
);
const DisputePage = Loadable(lazy(() => componentLoader(() => import("../pages/account/dispute/DisputePage"))));
const VerificationRequestPage = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/verification-request/VerificationRequestPage")))
);
const DisputeSuccess = Loadable(lazy(() => componentLoader(() => import("../pages/account/dispute/DisputeSuccess"))));
const PresetPlanCheckout = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment/PresetPlanCheckout")))
);
const GuestPaymentCheckout = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment/guest-payment/GuestPaymentCheckout")))
);
const GuestPaymentPlanCheckout = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment/guest-payment/GuestPaymentPlanCheckout")))
);
const GuestMatrixPaymentPlanCheckout = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment/guest-payment/GuestMatrixPaymentPlanCheckout")))
);
const PaymentSuccess = Loadable(lazy(() => componentLoader(() => import("../pages/account/success/PaymentSuccess"))));
const CalendarReminders = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/calendar-reminders/CalendarReminders")))
);
// PAYMENT PLAN
const ChoosePlan = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/installments-plan/ChoosePlan")))
);
const CustomPayment = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/PartialPayment")))
);
const MicroDownPayment = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/micro-down-payment/MicroDownPayment")))
);
const HardshipIntro = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/hardship-plan/HardshipIntro")))
);
const HardshipPlans = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/hardship-plan/HardshipPlans")))
);
const ReactivatePlan = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/reactivate-plan/ReactivatePlanWrapper")))
);
const Rewards = Loadable(lazy(() => componentLoader(() => import("../pages/account/rewards/RewardsIntro"))));
const TaxRefundWizard = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/tax-refund-wizard/TaxRefundWizard")))
);
const ExpiredCampaign = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/expired-campaign/ExpiredCampaign")))
);
const SummaryPage = Loadable(lazy(() => componentLoader(() => import("../pages/account/payment-plans/Summary"))));
const HardshipExtensionSummary = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/HardshipExtensionSummary")))
);
const HardshipPaymentAfterFailureSummary = Loadable(
  lazy(() => componentLoader(() => import("../pages/account/payment-plans/HardshipPaymentAfterFailureSummary")))
);
// ERRORS
const Error = Loadable(lazy(() => componentLoader(() => import("../pages/general/error/ErrorPage"))));
